import { AfterViewInit, Component, ElementRef, HostBinding, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Optional, Self, ViewChild } from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { MatFormFieldControl } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { Observable, Subject, debounceTime, fromEvent, startWith, switchMap, filter, tap, map } from "rxjs";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { LocationSuggestion, LocationsService } from "src/app/_services/locations.service";

@Component({
  selector: 'select-location',
  templateUrl: './select-location.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectLocationComponent,
      multi: true
    }
  ]
})
export class SelectLocationComponent implements MatFormFieldControl<LocationSuggestion>, OnDestroy, OnInit, AfterViewInit, ControlValueAccessor {
  
  static nextId = 0;
    
  @Input("formControlName") control: string;
  @Input("countryCode") countryCode: string | null;
  
  @HostBinding() id = `select-location-${SelectLocationComponent.nextId++}`;
  @ViewChild("input") input: ElementRef;
  @ViewChild(MatInput) matInput: MatInput;
    
  locationOptions = new Observable<LocationSuggestion[]>();
  stateChanges = new Subject<void>();
  onChange: (_: LocationSuggestion | null) => void = (location: LocationSuggestion | null) => {};
  onTouched: any;
  _placeholder: string = $localize `Start typing`;
  _value: LocationSuggestion | null;
  focused = false;
  disabled: boolean;
  _required = false;
  touched = false;
  autofilled = false;
  controlType = "select-location";
  
  userAriaDescribedBy = undefined; //TODO
  disableAutomaticLabeling: true; //TODO
  
  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private locationsService: LocationsService,
    @Optional() @Self() public ngControl: NgControl
    ) {
      
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }
  
  ngOnInit(): void {
  }
  
  ngAfterViewInit(): void {
    this.initLocationOptions();
  }
  
  private initLocationOptions() {
    
    
    
    this.locationOptions = fromEvent(this.input.nativeElement, 'input').pipe(
      startWith(''),
      debounceTime(500),
      map((eventt: any) => eventt.target?.value),
      filter(partialLabel => partialLabel !== null && typeof partialLabel === 'string'),
      switchMap(partialLabel => this.loadLocationSuggestions(partialLabel as string))
    );
  }
  
  private async loadLocationSuggestions(partialLabel: string | null): Promise<LocationSuggestion[]> {
    
    console.log(`Loading location suggestions for country=${this.countryCode} and partial label=${partialLabel}`)
    
    if (!partialLabel || partialLabel.length < 3 || !this.countryCode) {
      return [];
    }
    
    return await this.locationsService.listSuggestions(this.countryCode, partialLabel);
  }
  
  writeValue(location: LocationSuggestion | null): void {
    this.value = location;
  }
  
  registerOnChange(fn: (_: LocationSuggestion | null) => void): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  
  @Input()
  get required() {
    return this._required;
  }
  
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  
  onInputChange(event: Event): void {
    this.value = null;
  }
    
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  
  set value(location: LocationSuggestion | null) {
    this._value = location;
    this.onChange(location);
    this.stateChanges.next();
  }
  
  get value(): LocationSuggestion | null {
    return this._value;
  }
  
  onAutocompleteSelection(event: MatAutocompleteSelectedEvent): void {
    this.value = event.option.value;
  }
  
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  
  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.input.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }
  
  displayFn(suggestion: LocationSuggestion | null): string {
    
    if (!suggestion) {
      return '';
    }

    return suggestion.label;
  }
  
  get empty() {
    return !this.value;
  }
  
  ngOnDestroy() {
    this.stateChanges.complete();
  }
  
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
  
  get errorState(): boolean {
    return this.required && !this.value && this.touched;
  }
  
  setDescribedByIds(ids: string[]): void {
    //TODO
  }
    
  onContainerClick(event: MouseEvent): void {
    this.matInput.focus();
  }
}
